/**
 * <recipe-portions> — ingredient quantity scaling (P11).
 *
 * Live behaviour reproduced: the +/- stepper rescales every ingredient
 * quantity relative to the recipe's base portion count. Quantities are stored
 * server-side in `data-original` ("1 EL", "50 g", "0,5 TL", "nach Belieben");
 * the leading number (comma or dot decimals) is scaled, the rest of the string
 * is kept. Values without a leading number pass through untouched.
 *
 * Text is rewritten in place inside a fixed-flex-basis cell (see
 * article-recipe.css), so rescaling never changes layout.
 */
class RecipePortions extends HTMLElement {
  connectedCallback() {
    this.base = parseFloat(this.dataset.basePortions) || 1;
    this.input = this.querySelector('.recipe-portions__input');
    if (!this.input) return;

    this.addEventListener('click', (event) => {
      const button = event.target.closest('[data-portions-step]');
      if (!button) return;
      const next = this.currentPortions() + Number(button.dataset.portionsStep);
      this.setPortions(next);
    });

    this.input.addEventListener('change', () => {
      this.setPortions(this.currentPortions());
    });
  }

  currentPortions() {
    return parseFloat(String(this.input.value).replace(',', '.')) || this.base;
  }

  setPortions(portions) {
    const min = parseFloat(this.input.min) || 1;
    const max = parseFloat(this.input.max) || 20;
    const clamped = Math.min(max, Math.max(min, Math.round(portions)));
    this.input.value = clamped;
    const factor = clamped / this.base;
    this.querySelectorAll('[data-portions-quantity]').forEach((cell) => {
      cell.textContent = this.scaleQuantity(cell.dataset.original, factor);
    });
  }

  scaleQuantity(original, factor) {
    if (!original) return '';
    const match = original.match(/^(\d+(?:[.,]\d+)?)(.*)$/);
    if (!match) return original;
    const amount = parseFloat(match[1].replace(',', '.')) * factor;
    const rounded = Math.round(amount * 100) / 100;
    // German decimal comma, matching the stored source strings.
    const formatted = String(rounded).replace('.', ',');
    return `${formatted}${match[2]}`;
  }
}

if (!customElements.get('recipe-portions')) {
  customElements.define('recipe-portions', RecipePortions);
}
